import React, { FC, useCallback, useEffect } from "react";
import { Alert, Button, Dialog } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { setOnline, setOpen } from "@store/offlineBannerSlice";

const OfflineBanner: FC = () => {
  const { open, online } = useSelector(
    (state: RootState) => state.offlineBanner,
  );
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const onConnectionChange = useCallback(() => {
    dispatch(setOnline(navigator.onLine));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setOpen(!online));
  }, [online, dispatch]);

  useEffect(() => {
    const _onConnectionChange = onConnectionChange;

    window.addEventListener("load", _onConnectionChange);
    window.addEventListener("offline", _onConnectionChange);
    window.addEventListener("online", _onConnectionChange);

    return () => {
      window.addEventListener("load", _onConnectionChange);
      window.removeEventListener("offline", _onConnectionChange);
      window.removeEventListener("online", _onConnectionChange);
    };
  }, [dispatch, onConnectionChange]);

  const reload = () => {
    window.location.reload();
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-start",
        },
        "& .MuiAlert-action": {
          alignItems: "center",
          padding: 0,
          paddingLeft: "10px",
        },
      }}
    >
      <Alert
        severity="error"
        action={
          <Button
            autoFocus
            variant="outlined"
            sx={{
              whiteSpace: "nowrap",
              color: "#d32f2f",
              borderColor: "#d32f2f",
            }}
            onClick={reload}
          >
            {t("offlineBanner.reloadButton")}
          </Button>
        }
        sx={{
          ".MuiAlert-message": {
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        {t("offlineBanner.title")}
      </Alert>
    </Dialog>
  );
};

export default OfflineBanner;
